
export enum PhoneCopyFormat {
    E164 = "e164",
    TenDigit = "tenDigit",
    Parenthesized = "parenthesized",
    Dashed = "dashed",
}

export interface PhoneCopyFormatOption {
    format: PhoneCopyFormat;
    label: string;
}

export default class PhoneFormatter {

    static formatForDisplay(phone: string | null | undefined): string {
        if (!phone) return "";

        // Remove all non-digit characters
        const digitsOnly = phone.replace(/\D/g, "");

        // Handle 11-digit numbers starting with 1 (US country code)
        let digits = digitsOnly;
        if (digits.length === 11 && digits.startsWith("1")) {
            digits = digits.substring(1);
        }

        // Format as (XXX) XXX-XXXX if we have exactly 10 digits
        if (digits.length === 10) {
            const areaCode = digits.substring(0, 3);
            const prefix = digits.substring(3, 6);
            const lineNumber = digits.substring(6, 10);
            return `(${areaCode}) ${prefix}-${lineNumber}`;
        }

        // Return original value if not a standard US number
        return phone;
    }

    static formatForTwilio(phone: string | null | undefined): string {
        if (!phone) return "";

        // Remove all non-digit characters
        const digitsOnly = phone.replace(/\D/g, "");

        // Handle different lengths
        if (digitsOnly.length === 10) {
            // 10 digits - add +1
            return `+1${digitsOnly}`;
        } else if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
            // 11 digits starting with 1 - add +
            return `+${digitsOnly}`;
        }

        // For other cases, try to preserve original format if it had a +
        if (phone.startsWith("+")) {
            return `+${digitsOnly}`;
        }

        // Return digits only for non-standard formats
        return digitsOnly || phone;
    }

    static formatTenDigit(phone: string | null | undefined): string {
        if (!phone) return "";

        const digitsOnly = phone.replace(/\D/g, "");

        if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
            return digitsOnly.substring(1);
        }

        return digitsOnly || "";
    }

    static formatDashed(phone: string | null | undefined): string {
        if (!phone) return "";

        const tenDigit = PhoneFormatter.formatTenDigit(phone);
        if (tenDigit.length !== 10) return phone;

        return `${tenDigit.substring(0, 3)}-${tenDigit.substring(3, 6)}-${tenDigit.substring(6, 10)}`;
    }

    static formatAs(phone: string | null | undefined, format: PhoneCopyFormat): string {
        switch (format) {
            case PhoneCopyFormat.E164:
                return PhoneFormatter.formatForTwilio(phone);
            case PhoneCopyFormat.TenDigit:
                return PhoneFormatter.formatTenDigit(phone);
            case PhoneCopyFormat.Parenthesized:
                return PhoneFormatter.formatForDisplay(phone);
            case PhoneCopyFormat.Dashed:
                return PhoneFormatter.formatDashed(phone);
            default:
                return PhoneFormatter.formatForTwilio(phone);
        }
    }

    static copyFormatOptions(phone: string | null | undefined): PhoneCopyFormatOption[] {
        return [
            { format: PhoneCopyFormat.E164, label: PhoneFormatter.formatForTwilio(phone) || "+1XXXXXXXXXX" },
            { format: PhoneCopyFormat.TenDigit, label: PhoneFormatter.formatTenDigit(phone) || "XXXXXXXXXX" },
            { format: PhoneCopyFormat.Parenthesized, label: PhoneFormatter.formatForDisplay(phone) || "(XXX) XXX-XXXX" },
            { format: PhoneCopyFormat.Dashed, label: PhoneFormatter.formatDashed(phone) || "XXX-XXX-XXXX" },
        ];
    }
}

export function formatPhoneFilter(
    phone: string | null | undefined
): string {
    return PhoneFormatter.formatForDisplay(phone);
}

export function formatPhoneTwilioFilter(
    phone: string | null | undefined
): string {
    return PhoneFormatter.formatForTwilio(phone);
}
